import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { winners2025 } from 'data/winners2025';
import { winners2024 } from 'data/winners2024';
import { winners2023 } from 'data/winners2023';

interface Winner {
  id: string;
  name: string;
  organization: string;
  description: string;
  award: string;
  category?: 'individual' | 'team';
  year: number;
  projectLink?: string;
}

const SubmissionStatisticsSection: React.FC = () => {
  const [selectedYear, setSelectedYear] = useState<number>(2025);
  const years = [2023, 2024, 2025, 2026];

  const winnersData: Record<number, any[]> = {
    2025: winners2025,
    2024: winners2024,
    2023: winners2023,
  };

  const currentWinners = winnersData[selectedYear] || [];

  return (
    <section id="winners" className="bg-[#F7F8FA] py-12 sm:py-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="bg-white rounded-2xl shadow-smdd-card p-4 sm:p-6 md:p-8">
          <div className="text-center mb-6 sm:mb-8">
            <h2 className="text-2xl sm:text-3xl font-bai font-bold mb-3 sm:mb-4 text-gray-900">
              Awards Winner
            </h2>
            <p className="text-gray-600 text-base sm:text-lg mb-4 sm:mb-6">
              Meet the talented individuals and teams who have set new standards
              of excellence in digital innovation.
            </p>

            {/* Year Selector */}
            <div className="flex flex-wrap justify-center gap-3 sm:gap-4 mb-8 sm:mb-12">
              {years.map(year => (
                <button
                  key={year}
                  onClick={() => {
                    setSelectedYear(year);
                  }}
                  className={`cursor-pointer px-5 sm:px-8 py-2 rounded-full font-semibold transition-colors text-sm sm:text-base ${
                    selectedYear === year
                      ? 'bg-smdd-red text-white'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {year}
                </button>
              ))}
            </div>
          </div>

          <div className="space-y-3 sm:space-y-4 md:space-y-5">
            <div className="flex flex-wrap justify-center gap-6">
              {selectedYear === 2026 ? (
                <div className="w-full py-12 flex flex-col items-center justify-center text-center">
                  <div className="w-20 h-20 bg-red-50 rounded-full flex items-center justify-center mb-4 border border-red-100">
                    <svg
                      className="w-10 h-10 text-smdd-red opacity-80"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={1.5}
                        d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 00-2.456 2.456zM16.894 20.567L16.5 21.75l-.394-1.183a2.25 2.25 0 00-1.423-1.423L13.5 18.75l1.183-.394a2.25 2.25 0 001.423-1.423l.394-1.183.394 1.183a2.25 2.25 0 001.423 1.423l1.183.394-1.183.394a2.25 2.25 0 00-1.423 1.423z"
                      />
                    </svg>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">
                    Coming Soon
                  </h3>
                  <p className="text-gray-500 max-w-md">
                    The {selectedYear} Awards Winners will be announced
                    following the completion of the judging process. Stay tuned!
                  </p>
                </div>
              ) : (
                currentWinners.map((winner: Winner) => (
                  <Link
                    to={winner.year > 2024 ? `/winners/${winner.id}` : '#'}
                    key={winner.id}
                    className="group bg-white rounded-2xl p-6 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300 border border-gray-100 flex flex-col w-full md:w-[calc(50%-1.5rem)] lg:w-[calc(33.333%-1.5rem)] max-w-[400px] relative overflow-hidden"
                  >
                    {/* Top Bar Accent */}
                    <div className="absolute top-0 left-0 w-full h-1.5 bg-gradient-to-r from-red-200 via-smdd-red to-red-400 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>

                    {/* Badges Row */}
                    <div className="flex flex-wrap items-center justify-between gap-3 mb-5 mt-1">
                      <div className="inline-flex items-center gap-1.5 bg-red-50 text-smdd-red px-3 py-1.5 rounded-lg border border-red-100">
                        <svg
                          className="w-4 h-4"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                        </svg>
                        <span className="text-xs font-bold tracking-wide">
                          {winner.award}
                        </span>
                      </div>

                      {!!winner.category && (
                        <span
                          className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-[10px] font-bold uppercase tracking-wider ${
                            winner.category === 'individual'
                              ? 'bg-blue-50 text-blue-600 border border-blue-100'
                              : 'bg-gray-50 text-gray-600 border border-gray-200'
                          }`}
                        >
                          {winner.category === 'individual' ? (
                            <svg
                              className="w-3 h-3"
                              fill="currentColor"
                              viewBox="0 0 20 20"
                            >
                              <path d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" />
                            </svg>
                          ) : (
                            <svg
                              className="w-3 h-3"
                              fill="currentColor"
                              viewBox="0 0 20 20"
                            >
                              <path d="M9 6a3 3 0 11-6 0 3 3 0 016 0zM17 6a3 3 0 11-6 0 3 3 0 016 0zM12.93 17c.046-.327.07-.66.07-1a6.97 6.97 0 00-1.5-4.33A5 5 0 0119 16v1h-6.07zM6 11a5 5 0 015 5v1H1v-1a5 5 0 015-5z" />
                            </svg>
                          )}
                          {winner.category === 'individual'
                            ? 'Individual'
                            : 'Team'}
                        </span>
                      )}
                    </div>

                    {/* Card Content */}
                    <div className="flex-1 flex flex-col mb-4 mt-2">
                      <div className="flex items-center gap-3 mb-4">
                        <div
                          className={`flex-shrink-0 w-12 h-12 rounded-full flex items-center justify-center text-sm font-bold shadow-sm border ${
                            winner.category === 'individual'
                              ? 'bg-blue-50 text-blue-600 border-blue-100'
                              : 'bg-red-50 text-smdd-red border-red-100'
                          }`}
                        >
                          {winner.name
                            .split(' ')
                            .filter(Boolean)
                            .map(n => n[0])
                            .join('')
                            .substring(0, 2)
                            .toUpperCase()}
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="text-lg font-bold text-gray-900 group-hover:text-smdd-red transition-colors mb-0.5 truncate">
                            {winner.name}
                          </h3>
                          <p className="text-sm font-medium text-gray-500 truncate">
                            {winner.organization}
                          </p>
                        </div>
                      </div>
                      <p className="text-sm text-gray-600 leading-relaxed line-clamp-3">
                        {winner.description}
                      </p>
                    </div>

                    {/* Footer Action */}
                    {winner.projectLink && winner.year > 2024 && (
                      <div className="mt-auto border-t border-gray-50 pt-4 flex items-center text-sm font-bold text-gray-400 group-hover:text-smdd-red transition-colors">
                        See Project
                        <svg
                          className="w-4 h-4 ml-1 transform group-hover:translate-x-1 transition-transform"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M9 5l7 7-7 7"
                          />
                        </svg>
                      </div>
                    )}
                  </Link>
                ))
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default SubmissionStatisticsSection;
